import React, { useState } from 'react';
import { LifeBuoy, MessageCircle, Clock, CheckCircle } from 'lucide-react';
import { useTranslation } from '../../i18n/i18n';
import ReusableTable from '../../components/Tableau';

const SupportAdminPage = ({ lang }) => {
  const { t } = useTranslation(lang);

  const [tickets, setTickets] = useState([
    {
      id: 1,
      subject: "Problème de connexion",
      requester: "john.doe@example.com",
      status: "open",
      category: "Technical",
      assignedTo: "Support Team",
      createdAt: "2024-12-19T10:00:00Z",
      updatedAt: "2024-12-19T14:30:00Z",
      lastMessage: "L'utilisateur ne peut pas se connecter à son compte..."
    },
    {
      id: 2,
      subject: "Facturation incorrecte",
      requester: "sarah@company.com",
      status: "pending",
      category: "Billing",
      assignedTo: "Billing Team",
      createdAt: "2024-12-18T15:20:00Z",
      updatedAt: "2024-12-19T09:15:00Z",
      lastMessage: "Le montant facturé ne correspond pas au service..."
    },
    {
      id: 3,
      subject: "Demande de fonctionnalité",
      requester: "tech@startup.com",
      status: "closed",
      category: "Feature Request",
      assignedTo: "Product Team",
      createdAt: "2024-12-17T11:45:00Z",
      updatedAt: "2024-12-18T16:20:00Z",
      lastMessage: "Nous aimerions avoir une nouvelle fonctionnalité..."
    },
    {
      id: 4,
      subject: "Problème API",
      requester: "dev@agency.com",
      status: "open",
      category: "Technical",
      assignedTo: "Development Team",
      createdAt: "2024-12-19T08:30:00Z",
      updatedAt: "2024-12-19T12:45:00Z",
      lastMessage: "L'API retourne une erreur 500 sur l'endpoint..."
    }
  ]);

  // Statistiques
  const ticketStats = {
    total: tickets.length,
    open: tickets.filter(ticket => ticket.status === 'open').length,
    closed: tickets.filter(ticket => ticket.status === 'closed').length,
    pending: tickets.filter(ticket => ticket.status === 'pending').length
  };

  const stats = [
    {
      title: t('totalTickets'),
      value: ticketStats.total.toString(),
      description: t('allTickets'),
      icon: LifeBuoy,
      color: '#7c3aed',
      change: '+12%',
      isPositive: true
    },
    {
      title: t('openTickets'),
      value: ticketStats.open.toString(),
      description: t('needsAttention'),
      icon: MessageCircle,
      color: '#ef4444',
      change: '+5%',
      isPositive: false
    },
    {
      title: t('closedTickets'),
      value: ticketStats.closed.toString(),
      description: t('resolvedTickets'),
      icon: CheckCircle,
      color: '#10b981',
      change: '+8%',
      isPositive: true
    },
    {
      title: t('pendingTickets'),
      value: ticketStats.pending.toString(),
      description: t('waitingResponse'),
      icon: Clock,
      color: '#f59e0b',
      change: '+3%',
      isPositive: false
    }
  ];

  // Colonnes pour le tableau
  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'subject', label: t('subject') },
    { key: 'requester', label: t('requester') },
    { 
      key: 'status', 
      label: t('status'),
      render: (value) => (
        <span style={{
          padding: '4px 8px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          background: getStatusColor(value),
          color: 'white',
          textTransform: 'uppercase'
        }}>
          {t(value)}
        </span>
      )
    },
    { key: 'category', label: t('category') },
    { key: 'assignedTo', label: t('assignedTo') },
    { 
      key: 'createdAt', 
      label: t('createdAt'),
      render: (value) => formatDate(value)
    }
  ];


  const getStatusColor = (status) => {
    switch(status) {
      case 'open': return '#ef4444';
      case 'pending': return '#f59e0b';
      case 'closed': return '#10b981';
      default: return '#6b7280';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleDetails = (ticket) => {
    console.log('Détails du ticket:', ticket);
    // Navigation vers la page de détails du ticket
  };

  const handleRowClick = (ticket) => {
    handleDetails(ticket);
  };

  const styles = {
    container: {
      padding: '24px',
      background: '#f9fafb',
      minHeight: '100vh'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '32px'
    },
    titleContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px'
    },
    title: {
      fontSize: '28px',
      fontWeight: 'bold',
      color: '#1f2937',
      margin: 0
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px',
      marginBottom: '32px'
    },
    statCard: {
      background: 'white',
      padding: '20px',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease'
    },
    statIcon: {
      width: '56px',
      height: '56px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    },
    statContent: {
      flex: 1
    },
    statValue: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#1f2937',
      marginBottom: '4px'
    },
    statTitle: {
      fontSize: '14px',
      color: '#6b7280',
      marginBottom: '4px',
      fontWeight: '600'
    },
    statDescription: {
      fontSize: '12px',
      color: '#9ca3af'
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.titleContainer}>
          <LifeBuoy size={32} color="#7c3aed" />
          <h1 style={styles.title}>{t('supportAdmin')}</h1>
        </div>
      </div>

      {/* Cartes de statistiques */}
      <div style={styles.statsGrid}>
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div
              key={index}
              style={styles.statCard}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
              }}
            >
              <div style={{
                ...styles.statIcon,
                background: `linear-gradient(135deg, ${stat.color} 0%, ${stat.color}dd 100%)`
              }}>
                <Icon size={28} color="white" />
              </div>
              <div style={styles.statContent}>
                <div style={styles.statValue}>{stat.value}</div>
                <div style={styles.statTitle}>{stat.title}</div>
                <div style={styles.statDescription}>{stat.description}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tableau des tickets */}
      <ReusableTable
        data={tickets}
        columns={columns}
        onDetails={handleDetails}
        onRowClick={handleRowClick}
        lang={lang}
        itemsPerPageOptions={[10, 20, 50]}
        colors={{
          headerBg: '#7c3aed',
          buttonDetails: '#10b981'
        }}
        showEditButton={false}
        showDeleteButton={false}
      />
    </div>
  );
};

export default SupportAdminPage;